import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { Megaphone, UserCircle, Store, Settings, Users, PiggyBank, HandCoins, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
const VITAMIN_IMG = 'https://cdn.magicpatterns.com/uploads/6qBs8L82XDsgKE2TogCsHv/ChatGPT_Image_21_Mei_2026,_16.05.32.png';
export function ComparisonSection() {
  const {
    t
  } = useLanguage();
  return <section id="ecommerce-problem" className="py-24 bg-gradient-to-b from-secondary/30 to-white relative overflow-hidden" data-id="element-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-4">
          <p className="text-primary font-bold tracking-widest text-sm uppercase mb-4" data-id="element-5">
            {t.comparison.label}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-text-main leading-tight max-w-3xl mx-auto" data-id="element-6">
            {t.comparison.title}
          </h2>
        </div>

        {/* Comparison Cards Container */}
        <div className="relative max-w-6xl mx-auto" data-id="element-7">
          {/* VS Badge (Absolute Center) */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white rounded-full items-center justify-center text-primary font-extrabold text-2xl z-20 shadow-2xl border-4 border-primary" data-id="element-8">
            VS
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-6" data-id="element-9">
            {/* LEFT CARD: Old Ecommerce */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl border-2 border-orange-300 shadow-xl overflow-hidden relative flex flex-col" data-id="element-10">
              {/* Badge */}
              <div className="flex justify-center pt-6" data-id="element-11">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold px-6 py-2 rounded-full text-sm shadow-lg whitespace-nowrap flex items-center gap-2" data-id="element-12">
                  <AlertTriangle size={14} data-id="element-13" />
                  {t.comparison.oldEcommerce.badge}
                </div>
              </div>

              <div className="p-8 pt-6 flex-grow" data-id="element-14">
                <h3 className="text-center font-bold text-base md:text-lg text-red-700 mb-8 leading-tight" data-id="element-15">
                  {t.comparison.oldEcommerce.subtitle}
                </h3>

                <div className="flex flex-col items-center gap-6" data-id="element-16">
                  {/* Product Image */}
                  <div className="relative" data-id="element-17">
                    <div className="w-56 h-56 md:w-64 md:h-64 bg-white rounded-2xl flex items-center justify-center p-1 shadow-md border border-orange-100" data-id="element-18">
                      <img src={VITAMIN_IMG} alt="Vitamin Product" className="w-full h-full object-contain" data-id="element-19" />
                    </div>
                    {/* Floating cost indicators (decorative) */}
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg rotate-12" data-id="element-20">
                      {t.comparison.extraCost}
                    </div>
                  </div>

                  <div className="text-center" data-id="element-21">
                    <p className="text-[10px] font-bold text-text-muted tracking-wider" data-id="element-22">
                      {t.comparison.oldEcommerce.priceLabel}
                    </p>
                    <p className="text-xl font-bold text-text-main" data-id="element-23">
                      Rp 100.000
                    </p>
                  </div>

                  {/* Cost List */}
                  <div className="w-full space-y-2.5 mt-2" data-id="element-24">
                    <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-red-100" data-id="element-25">
                      <div className="flex items-center gap-3" data-id="element-26">
                        <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center text-red-500 shrink-0" data-id="element-27">
                          <Megaphone size={18} data-id="element-28" />
                        </div>
                        <p className="text-xs font-bold text-text-main" data-id="element-29">
                          {t.comparison.oldEcommerce.ads}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-red-500 whitespace-nowrap" data-id="element-30">
                        + Rp 20.000
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-red-100" data-id="element-31">
                      <div className="flex items-center gap-3" data-id="element-32">
                        <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center text-red-500 shrink-0" data-id="element-33">
                          <UserCircle size={18} data-id="element-34" />
                        </div>
                        <p className="text-xs font-bold text-text-main" data-id="element-35">
                          {t.comparison.oldEcommerce.influencer}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-red-500 whitespace-nowrap" data-id="element-36">
                        + Rp 15.000
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-red-100" data-id="element-37">
                      <div className="flex items-center gap-3" data-id="element-38">
                        <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center text-red-500 shrink-0" data-id="element-39">
                          <Store size={18} data-id="element-40" />
                        </div>
                        <p className="text-xs font-bold text-text-main" data-id="element-41">
                          {t.comparison.oldEcommerce.marketplace}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-red-500 whitespace-nowrap" data-id="element-42">
                        + Rp 15.000
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 bg-white p-3 rounded-xl border border-red-100" data-id="element-43">
                      <div className="flex items-center gap-3" data-id="element-44">
                        <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center text-red-500 shrink-0" data-id="element-45">
                          <Settings size={18} data-id="element-46" />
                        </div>
                        <p className="text-xs font-bold text-text-main" data-id="element-47">
                          {t.comparison.oldEcommerce.platform}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-red-500 whitespace-nowrap" data-id="element-48">
                        + Rp 10.000
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Bar */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-5 flex items-center justify-center gap-4 text-white" data-id="element-49">
                <div className="text-center" data-id="element-50">
                  <p className="text-xs font-semibold opacity-90 tracking-wider" data-id="element-51">
                    {t.comparison.oldEcommerce.totalLabel}
                  </p>
                  <p className="text-3xl font-extrabold" data-id="element-52">Rp 160.000</p>
                </div>
                <TrendingUp size={32} className="opacity-90" data-id="element-53" />
              </div>
            </motion.div>

            {/* RIGHT CARD: Tokoteta */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-blue-50 to-white rounded-3xl border-2 border-primary shadow-xl overflow-hidden relative flex flex-col" data-id="element-54">
              {/* Badge */}
              <div className="flex justify-center pt-6" data-id="element-55">
                <div className="bg-primary text-white font-bold px-6 py-2 rounded-full text-sm shadow-lg whitespace-nowrap flex items-center gap-2" data-id="element-56">
                  <span className="bg-white text-primary rounded-md px-1.5 text-xs font-extrabold" data-id="element-57">
                    tt
                  </span>
                  TOKOTETA
                </div>
              </div>

              <div className="p-8 pt-6 flex-grow" data-id="element-58">
                <h3 className="text-center font-bold text-base md:text-lg text-primary mb-8 leading-tight" data-id="element-59">
                  {t.comparison.tokoteta.subtitle}
                </h3>

                <div className="flex flex-col items-center gap-6" data-id="element-60">
                  {/* Product Image */}
                  <div className="relative" data-id="element-61">
                    <div className="w-56 h-56 md:w-64 md:h-64 bg-white rounded-2xl flex items-center justify-center p-1 shadow-md border border-blue-100" data-id="element-62">
                      <img src={VITAMIN_IMG} alt="Vitamin Product" className="w-full h-full object-contain" data-id="element-63" />
                    </div>
                    {/* Floating savings badge */}
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg rotate-12" data-id="element-64">
                      {t.comparison.savings}
                    </div>
                  </div>

                  <div className="text-center" data-id="element-65">
                    <p className="text-[10px] font-bold text-text-muted tracking-wider" data-id="element-66">
                      {t.comparison.tokoteta.priceLabel}
                    </p>
                    <p className="text-xl font-bold text-text-main" data-id="element-67">
                      Rp 100.000
                    </p>
                  </div>

                  {/* Benefit List */}
                  <div className="w-full space-y-2.5 mt-2" data-id="element-68">
                    <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100" data-id="element-69">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-primary shrink-0" data-id="element-70">
                        <Users size={18} data-id="element-71" />
                      </div>
                      <div className="flex-grow" data-id="element-72">
                        <p className="text-xs font-bold text-text-main" data-id="element-73">
                          {t.comparison.tokoteta.community}
                        </p>
                        <p className="text-[10px] text-text-muted" data-id="element-74">
                          {t.comparison.tokoteta.communityDesc}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100" data-id="element-75">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-primary shrink-0" data-id="element-76">
                        <PiggyBank size={18} data-id="element-77" />
                      </div>
                      <div className="flex-grow" data-id="element-78">
                        <p className="text-xs font-bold text-text-main" data-id="element-79">
                          {t.comparison.tokoteta.platform}
                        </p>
                        <p className="text-[10px] text-text-muted" data-id="element-80">
                          {t.comparison.tokoteta.platformDesc}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-blue-100" data-id="element-81">
                      <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-primary shrink-0" data-id="element-82">
                        <HandCoins size={18} data-id="element-83" />
                      </div>
                      <div className="flex-grow" data-id="element-84">
                        <p className="text-xs font-bold text-text-main" data-id="element-85">
                          {t.comparison.tokoteta.userCommission}
                        </p>
                        <p className="text-[10px] text-text-muted" data-id="element-86">
                          {t.comparison.tokoteta.userCommissionDesc}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-green-50 p-3 rounded-xl border-2 border-green-200" data-id="element-87">
                      <div className="w-9 h-9 rounded-lg bg-green-500 flex items-center justify-center text-white shrink-0 font-bold text-xs" data-id="element-88">
                        Rp
                      </div>
                      <div className="flex-grow" data-id="element-89">
                        <p className="text-xs font-bold text-green-700" data-id="element-90">
                          {t.comparison.tokoteta.youEarn}
                        </p>
                        <p className="text-[10px] text-green-600" data-id="element-91">
                          {t.comparison.tokoteta.youEarnDesc}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Bar */}
              <div className="bg-primary p-5 flex items-center justify-center gap-4 text-white" data-id="element-92">
                <div className="text-center" data-id="element-93">
                  <p className="text-xs font-semibold opacity-90 tracking-wider" data-id="element-94">
                    {t.comparison.tokoteta.totalLabel}
                  </p>
                  <p className="text-3xl font-extrabold" data-id="element-95">Rp 110.000</p>
                </div>
                <TrendingDown size={32} className="text-green-300" data-id="element-96" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}